import { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { IEvent, IFee } from "../../../interfaces/models/IEvent";
import WSGCheck from "../../../components/Atomos/Icons/WSGCheck";
import CalendarInput from "../../../components/Atomos/Inputs/CalendarInput";
import { timestampToDateString } from "../../../utils/dates";
import TextArea from "../../../components/Atomos/Inputs/TextArea";
import Input from "../../../components/Atomos/Inputs/Input";
import LegacyButton from "../../../components/Atomos/Buttons/LegacyButton";
import Close from "../../../components/Atomos/Icons/Close";
import SaveChanges from "../../../components/Atomos/Icons/EditMode/SaveChanges";
import { updateEvent, getEventById } from "../../../Services/Event";
import { toastError, toastPromise } from "../../../utils/toast";
import { NewEvent } from "../../../redux/states/event.state";
import { CURRENCY_OPTIONS } from "../../../utils/const";
import Dropdown from "../../../components/Atomos/DropDown/Dropdown";

export default function StaffRegistrationEditMode({
  event,
  onEventUpdate,
  onEditClick,
}: {
  event: IEvent;
  onEventUpdate: (event: IEvent) => void;
  onEditClick?: () => void;
}) {
  const dispatch = useDispatch();

  const [waitListEnabled, setWaitListEnabled] = useState(false);
  const [eventFeesEnabled, setEventFeesEnabled] = useState(false);
  const [registrationExpirationEnabled, setRegistrationExpirationEnabled] =
    useState(false);
  const [registrationMessage, setRegistrationMessage] = useState("");
  const [registrationExpiration, setRegistrationExpiration] = useState("");
  const [registrationLink, setRegistrationLink] = useState("");
  const [addGuestCapacityEnabled, setAddGuestCapacityEnabled] = useState(false);
  const [eventCapacity, setEventCapacity] = useState("");
  const [firmCapacity, setFirmCapacity] = useState("");
  const [waitListMessage, setWaitListMessage] = useState("");
  const [capacityMessage, setCapacityMessage] = useState("");
  const [disclaimCapacityMessage, setDisclaimCapacityMessage] = useState("");
  const [addLateFeesEnabled, setAddLateFeesEnabled] = useState(false);
  const [memberFee, setMemberFee] = useState("");
  const [memberLateFee, setMemberLateFee] = useState("");
  const [guestLateFee, setGuestLateFee] = useState("");
  const [guestFee, setGuestFee] = useState("");
  const [programFees, setProgramFees] = useState<Record<number, string>>({});
  const [programGuestFees, setProgramGuestFees] = useState<Record<number, string>>({});
  const [cancellationPolicy, setCancellationPolicy] = useState("");
  const [partialRefund, setPartialRefund] = useState("");
  const [fullRefundEndDate, setFullRefundEndDate] = useState("");
  const [partialRefundEndDate, setPartialRefundEndDate] = useState("");
  const [currency, setCurrency] = useState("");
  const [guestCapacity, setGuestCapacity] = useState("");

  const [errors, setErrors] = useState({
    eventCapacity: false,
    firmCapacity: false,
    guestCapacity: false,
    waitListMessage: false,
    capacityMessage: false,
    disclaimCapacityMessage: false,
    currency: false,
    memberFee: false,
    memberLateFee: false,
    guestLateFee: false,
    cancellationPolicy: false,
    partialRefund: false,
    fullRefundEndDate: false,
    partialRefundEndDate: false,
    registrationExpiration: false,
  });

  const saveSectionStateToLocalStorage = (
    section: "waitList" | "fees" | "registration",
    value: boolean
  ) => {
    if (!event?.id) {
      return;
    }
    
    const storageKey = `event_registration_sections_${event.id}`;
    const savedState = localStorage.getItem(storageKey);
    let sectionStates = {
      waitList: section === "waitList" ? value : waitListEnabled,
      fees: section === "fees" ? value : eventFeesEnabled,
      registration: section === "registration" ? value : registrationExpirationEnabled,
    };
    
    if (savedState) {
      try {
        const parsed = JSON.parse(savedState);
        sectionStates = {
          ...parsed,
          [section]: value,
        };
      } catch (e) {
        sectionStates[section] = value;
      }
    } else {
      sectionStates[section] = value;
    }
    
    localStorage.setItem(storageKey, JSON.stringify(sectionStates));
  };

  useEffect(() => {
    if (event) {
      const storageKey = `event_registration_sections_${event.id}`;
      const savedState = localStorage.getItem(storageKey);
      let sectionStates = {
        waitList: false,
        fees: false,
        registration: false,
      };
      
      if (event.registrationSettings) {
        sectionStates = {
          waitList: event.registrationSettings.waitListEnabled ?? false,
          fees: event.registrationSettings.feesEnabled ?? false,
          registration: event.registrationSettings.registrationExpirationEnabled ?? false,
        };
      } 
      else if (savedState) {
        try {
          sectionStates = JSON.parse(savedState);
        } catch (e) {
        }
      }
      
      setWaitListEnabled(sectionStates.waitList);
      setEventFeesEnabled(sectionStates.fees);
      setRegistrationExpirationEnabled(sectionStates.registration);
      
      setRegistrationMessage(event.registrationMessage || "");
      setRegistrationExpiration(
        event.registrationExpiration
          ? timestampToDateString(event.registrationExpiration)
          : ""
      );
      setRegistrationLink(event.registrationLink || "");

      if (event.waitList) {
        setAddGuestCapacityEnabled(!!event.waitList.guestCapacity);
        setEventCapacity(event.waitList.eventCapacity?.toString() || "");
        setFirmCapacity(event.waitList.firmCapacity?.toString() || "");
        setGuestCapacity(event.waitList.guestCapacity?.toString() || "");
        setWaitListMessage(event.waitList.waitListMessage || "");
        setCapacityMessage(event.waitList.capacityMessage || "");
        setDisclaimCapacityMessage(
          event.waitList.disclaimCapacityMessage || ""
        );
      }

      if (event.fee) {
        setAddLateFeesEnabled(!!event.fee.guestLateFee || !!event.fee.lateFee);
        setMemberFee(
          event.fee.memberFee?.toString() || event.fee.fee?.toString() || ""
        );
        setMemberLateFee(event.fee.lateFee?.toString() || "");
        setGuestFee(event.fee.guestFee?.toString() || "");
        setGuestLateFee(
          event.fee.guestLateFee?.toString() ||
            ""
        );
        setCancellationPolicy(event.fee.cancellationPolicy || "");
        setPartialRefund(event.fee.partialRefund?.toString() || "");
        setFullRefundEndDate(
          event.fee.fullRefundEndDate
            ? timestampToDateString(event.fee.fullRefundEndDate)
            : ""
        );
        setPartialRefundEndDate(
          event.fee.partialRefundEndDate
            ? timestampToDateString(event.fee.partialRefundEndDate)
            : ""
        );
        setCurrency(event.fee.currency || "USD");
      } else {
        setCurrency("USD");
      }

      if (event.program) {
        const fees: Record<number, string> = {};
        const guestFees: Record<number, string> = {};
        event.program.forEach((program, index) => {
          if (program.requiredRegistration) {
            fees[index] = program.fee?.toString() || "";
            guestFees[index] = program.specialEventGuestFee != null ? program.specialEventGuestFee.toString() : "";
          }
        });
        setProgramFees(fees);
        setProgramGuestFees(guestFees);
      }
    }
  }, [event]);

  const convertDateStringToTimestamp = (
    dateString: string
  ): number | undefined => {
    if (!dateString || dateString.trim() === "") {
      return undefined;
    }

    const mmddyyyy = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(dateString);
    if (mmddyyyy) {
      const month = parseInt(mmddyyyy[1], 10) - 1;
      const day = parseInt(mmddyyyy[2], 10);
      const year = parseInt(mmddyyyy[3], 10);
      const date = new Date(Date.UTC(year, month, day, 0, 0, 0));
      if (!isNaN(date.getTime())) {
        return Math.floor(date.getTime() / 1000);
      }
    }

    const date = new Date(dateString);
    if (!isNaN(date.getTime())) {
      return Math.floor(date.getTime() / 1000);
    }

    return undefined;
  };

  const addOneDayToDateString = (dateString: string): string => {
    if (!dateString || dateString.trim() === "") {
      return "";
    }

    const mmddyyyy = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(dateString);
    if (mmddyyyy) {
      const month = parseInt(mmddyyyy[1], 10) - 1;
      const day = parseInt(mmddyyyy[2], 10);
      const year = parseInt(mmddyyyy[3], 10);
      const date = new Date(Date.UTC(year, month, day, 0, 0, 0));
      if (!isNaN(date.getTime())) {
        date.setUTCDate(date.getUTCDate() + 1);
        const utcYear = date.getUTCFullYear();
        const utcMonth = String(date.getUTCMonth() + 1).padStart(2, "0");
        const utcDay = String(date.getUTCDate()).padStart(2, "0");
        return `${utcMonth}/${utcDay}/${utcYear}`;
      }
    }

    const date = new Date(dateString);
    if (!isNaN(date.getTime())) {
      // Add one day in UTC
      date.setUTCDate(date.getUTCDate() + 1);
      const utcYear = date.getUTCFullYear();
      const utcMonth = String(date.getUTCMonth() + 1).padStart(2, "0");
      const utcDay = String(date.getUTCDate()).padStart(2, "0");
      return `${utcMonth}/${utcDay}/${utcYear}`;
    }

    return "";
  };

  const partialRefundStartDate = fullRefundEndDate
    ? addOneDayToDateString(fullRefundEndDate)
    : "";
  const noRefundStartDate = partialRefundEndDate
    ? addOneDayToDateString(partialRefundEndDate)
    : "";

  const validateFields = (): boolean => {
    const newErrors = {
      eventCapacity: false,
      firmCapacity: false,
      guestCapacity: false,
      waitListMessage: false,
      capacityMessage: false,
      disclaimCapacityMessage: false,
      currency: false,
      memberFee: false,
      memberLateFee: false,
      guestLateFee: false,
      cancellationPolicy: false,
      partialRefund: false,
      fullRefundEndDate: false,
      partialRefundEndDate: false,
      registrationExpiration: false,
    };

    let hasErrors = false;

    if (waitListEnabled) {
      if (
        !eventCapacity ||
        eventCapacity.trim() === "" ||
        isNaN(parseInt(eventCapacity))
      ) {
        newErrors.eventCapacity = true;
        hasErrors = true;
      }
      if (
        !firmCapacity ||
        firmCapacity.trim() === "" ||
        isNaN(parseInt(firmCapacity))
      ) {
        newErrors.firmCapacity = true;
        hasErrors = true;
      }
      if (
        addGuestCapacityEnabled &&
        (!guestCapacity ||
          guestCapacity.trim() === "" ||
          isNaN(parseInt(guestCapacity)))
      ) {
        newErrors.guestCapacity = true;
        hasErrors = true;
      }
      if (!waitListMessage || waitListMessage.trim() === "") {
        newErrors.waitListMessage = true;
        hasErrors = true;
      }
      if (!capacityMessage || capacityMessage.trim() === "") {
        newErrors.capacityMessage = true;
        hasErrors = true;
      }
      if (!disclaimCapacityMessage || disclaimCapacityMessage.trim() === "") {
        newErrors.disclaimCapacityMessage = true;
        hasErrors = true;
      }
    }

    if (eventFeesEnabled) {
      if (!currency || currency.trim() === "") {
        newErrors.currency = true;
        hasErrors = true;
      }
      if (
        !memberFee ||
        memberFee.trim() === "" ||
        isNaN(parseFloat(memberFee))
      ) {
        newErrors.memberFee = true;
        hasErrors = true;
      }
      if (
        addLateFeesEnabled &&
        (!memberLateFee ||
          memberLateFee.trim() === "" ||
          isNaN(parseFloat(memberLateFee)))
      ) {
        newErrors.memberLateFee = true;
        hasErrors = true;
      }
      if (
        addLateFeesEnabled &&
        guestFee &&
        guestFee.trim() !== "" &&
        (!guestLateFee ||
          guestLateFee.trim() === "" ||
          isNaN(parseFloat(guestLateFee)))
      ) {
        newErrors.guestLateFee = true;
        hasErrors = true;
      }
      // Validación de formato para partialRefund solo si tiene valor
      if (partialRefund && partialRefund.trim() !== "") {
        if (isNaN(parseFloat(partialRefund))) {
          newErrors.partialRefund = true;
          hasErrors = true;
        }
      }
      // Validación de formato para fullRefundEndDate solo si tiene valor
      if (fullRefundEndDate && fullRefundEndDate.trim() !== "") {
        const timestamp = convertDateStringToTimestamp(fullRefundEndDate);
        if (timestamp === undefined || isNaN(timestamp)) {
          newErrors.fullRefundEndDate = true;
          hasErrors = true;
        }
      }
      // Validación de formato para partialRefundEndDate solo si tiene valor
      if (partialRefundEndDate && partialRefundEndDate.trim() !== "") {
        const timestamp = convertDateStringToTimestamp(partialRefundEndDate);
        if (timestamp === undefined || isNaN(timestamp)) {
          newErrors.partialRefundEndDate = true;
          hasErrors = true;
        }
      }
    }

    if (registrationExpirationEnabled) {
      if (!registrationExpiration || registrationExpiration.trim() === "") {
        newErrors.registrationExpiration = true;
        hasErrors = true;
      } else {
        const timestamp = convertDateStringToTimestamp(registrationExpiration);
        if (timestamp === undefined || isNaN(timestamp)) {
          newErrors.registrationExpiration = true;
          hasErrors = true;
        }
      }
    }

    setErrors(newErrors);
    return !hasErrors;
  };

  const handleSave = async () => {
    if (!event || !event.id) {
      toastError("Event ID is required to save changes");
      return;
    }

    if (!validateFields()) {
      toastError("Please fill in all required fields");
      return;
    }

    try {
      const updatedEvent: IEvent = {
        ...event,
        registrationMessage: registrationMessage.trim() || undefined,
        registrationLink: registrationLink.trim() || undefined,
        registrationSettings: {
          waitListEnabled: waitListEnabled,
          feesEnabled: eventFeesEnabled,
          registrationExpirationEnabled: registrationExpirationEnabled,
        },
      };

      if (registrationExpirationEnabled) {
        if (registrationExpiration && registrationExpiration.trim() !== "") {
          const timestamp = convertDateStringToTimestamp(
            registrationExpiration
          );
          if (timestamp !== undefined && !isNaN(timestamp)) {
            updatedEvent.registrationExpiration = timestamp;
          }
        }
      }

      if (waitListEnabled) {
        updatedEvent.waitList = {
          eventCapacity: parseInt(eventCapacity) || 0,
          firmCapacity: parseInt(firmCapacity) || 0,
          ...(addGuestCapacityEnabled &&
            guestCapacity &&
            guestCapacity.trim() !== "" && {
              guestCapacity: parseInt(guestCapacity) || 0,
            }),
          ...(waitListMessage.trim() && {
            waitListMessage: waitListMessage.trim(),
          }),
          ...(capacityMessage.trim() && {
            capacityMessage: capacityMessage.trim(),
          }),
          ...(disclaimCapacityMessage.trim() && {
            disclaimCapacityMessage: disclaimCapacityMessage.trim(),
          }),
        };
      }

      const hasFeeFields =
        eventFeesEnabled &&
        ((memberFee && memberFee.trim() !== "") ||
          (guestFee && guestFee.trim() !== "") ||
          (addLateFeesEnabled &&
            memberLateFee &&
            memberLateFee.trim() !== "") ||
          (addLateFeesEnabled && guestLateFee && guestLateFee.trim() !== "") ||
          (Object.values(programFees).some(fee => fee && fee.trim() !== "")) ||
          (Object.values(programGuestFees).some(fee => fee && fee.trim() !== "")));

      const hasCancellationFields =
        eventFeesEnabled &&
        ((cancellationPolicy && cancellationPolicy.trim() !== "") ||
          (partialRefund && partialRefund.trim() !== "") ||
          (fullRefundEndDate && fullRefundEndDate.trim() !== "") ||
          (partialRefundEndDate && partialRefundEndDate.trim() !== ""));

      if (eventFeesEnabled && (hasFeeFields || hasCancellationFields)) {
        const feeObj: IFee = {
          currency: currency || "USD",
        };

        if (memberFee && memberFee.trim() !== "") {
          const memberFeeValue = parseFloat(memberFee);
          if (!isNaN(memberFeeValue)) {
            feeObj.memberFee = memberFeeValue;
          }
        }

        if (guestFee && guestFee.trim() !== "") {
          const guestFeeValue = parseFloat(guestFee);
          if (!isNaN(guestFeeValue)) {
            feeObj.guestFee = guestFeeValue;
          }
        }

        if (
          addLateFeesEnabled &&
          memberLateFee &&
          memberLateFee.trim() !== ""
        ) {
          const memberLateFeeValue = parseFloat(memberLateFee);
          if (!isNaN(memberLateFeeValue)) {
            feeObj.lateFee = memberLateFeeValue;
          }
        }

        if (
          addLateFeesEnabled &&
          guestLateFee &&
          guestLateFee.trim() !== ""
        ) {
          const guestLateFeeValue = parseFloat(guestLateFee);
          if (!isNaN(guestLateFeeValue)) {
            feeObj.guestLateFee = guestLateFeeValue;
          }
        }


        if (cancellationPolicy && cancellationPolicy.trim() !== "") {
          feeObj.cancellationPolicy = cancellationPolicy.trim();
        }

        if (partialRefund && partialRefund.trim() !== "") {
          const partialRefundValue = parseFloat(partialRefund);
          if (!isNaN(partialRefundValue)) {
            feeObj.partialRefund = partialRefundValue;
          }
        }

        if (fullRefundEndDate && fullRefundEndDate.trim() !== "") {
          const timestamp = convertDateStringToTimestamp(fullRefundEndDate);
          if (timestamp !== undefined && !isNaN(timestamp)) {
            feeObj.fullRefundEndDate = timestamp;
          }
        }

        if (partialRefundEndDate && partialRefundEndDate.trim() !== "") {
          const timestamp = convertDateStringToTimestamp(partialRefundEndDate);
          if (timestamp !== undefined && !isNaN(timestamp)) {
            feeObj.partialRefundEndDate = timestamp;
          }
        }

        if (fullRefundEndDate && fullRefundEndDate.trim() !== "") {
          const fullRefundTimestamp =
            convertDateStringToTimestamp(fullRefundEndDate);
          if (
            fullRefundTimestamp !== undefined &&
            !isNaN(fullRefundTimestamp)
          ) {
            feeObj.partialRefundStartDate = fullRefundTimestamp + 86400;
          }
        }

        if (partialRefundEndDate && partialRefundEndDate.trim() !== "") {
          const partialRefundTimestamp =
            convertDateStringToTimestamp(partialRefundEndDate);
          if (
            partialRefundTimestamp !== undefined &&
            !isNaN(partialRefundTimestamp)
          ) {
            feeObj.noRefundDate = partialRefundTimestamp + 86400;
          }
        }

        updatedEvent.fee = feeObj;
      }

      if (event.program && updatedEvent.program) {
        updatedEvent.program = updatedEvent.program.map((program, index) => {
          if (program.requiredRegistration && programFees[index] !== undefined) {
            const feeValue = programFees[index]?.trim();
            const guestFeeValue = programGuestFees[index]?.trim();
            return {
              ...program,
              fee: feeValue && feeValue !== "" ? parseFloat(feeValue) : undefined,
              specialEventGuestFee: guestFeeValue && guestFeeValue !== "" ? parseFloat(guestFeeValue) : null,
            };
          }
          return program;
        });
      }

      const promise = updateEvent(updatedEvent);
      await toastPromise(promise, {
        pending: "Saving registration data...",
        success: "Registration data saved successfully!",
        error: "Failed to save registration data",
      });

      const reloadedEvent = await getEventById(event.id);

      if (reloadedEvent) {
        dispatch(NewEvent(reloadedEvent));

        if (onEventUpdate) {
          onEventUpdate(reloadedEvent);
        }
      }

      if (onEditClick) {
        onEditClick();
      }
    } catch (error) {
      toastError(error as string);
    }
  };

  const handleCancel = () => {
    if (onEditClick) {
      onEditClick();
    }
  };
  return (
    <>
      <p className="text-Default font-decimal text-[32px] font-medium leading-[36px] text-left mb-6">
        General Registration Settings
      </p>
      {(event.setupType === "webinar" || event.setupType === "groupWebinar" || event.memberEventsTab) && (
        <div className="w-full mb-6">
          <p className="text-primary font-decimal text-[26px] font-medium leading-[36px] text-left mb-6">
            Registration Link
          </p>
          <Input
            title="Registration Link"
            value={registrationLink}
            onChange={(e: any) => setRegistrationLink(e.target.value)}
            error={false}
            placeholder="https://example.com/register"
          />
        </div>
      )}
      <div className="w-full mb-6">
        <p className="text-gray-font font-medium text-base mb-2">
          Registration Settings
        </p>
        <div className="flex flex-col lg:flex-row items-start justify-start md:gap-8 gap-4">
          <div className="flex flex-row items-center gap-2">
            <div
              onClick={() => {
                const newValue = !waitListEnabled;
                setWaitListEnabled(newValue);
                saveSectionStateToLocalStorage("waitList", newValue);
                
                if (!newValue) {
                  setErrors({
                    ...errors,
                    eventCapacity: false,
                    firmCapacity: false,
                    guestCapacity: false,
                    waitListMessage: false,
                    capacityMessage: false,
                    disclaimCapacityMessage: false,
                  });
                }
              }}
              className={`stroke-gray-font stroke-2 ${
                !waitListEnabled ? "fill-neutral" : "fill-secondary"
              } h-5 w-5 min-w-[20px] min-h-[20px] cursor-pointer`}
            >
              <WSGCheck check={waitListEnabled} />
            </div>
            <p className="text-gray-font font-[400] text-base">Add Wait List</p>
          </div>
          <div className="flex flex-row items-center gap-2">
            <div
              onClick={() => {
                const newValue = !eventFeesEnabled;
                setEventFeesEnabled(newValue);
                saveSectionStateToLocalStorage("fees", newValue);
                
                if (!newValue) {
                  setErrors({
                    ...errors,
                    currency: false,
                    memberFee: false,
                    memberLateFee: false,
                    guestLateFee: false,
                    cancellationPolicy: false,
                    partialRefund: false,
                    fullRefundEndDate: false,
                    partialRefundEndDate: false,
                  });
                }
              }}
              className={`stroke-gray-font stroke-2 ${
                !eventFeesEnabled ? "fill-neutral" : "fill-secondary"
              } h-5 w-5 min-w-[20px] min-h-[20px] cursor-pointer`}
            >
              <WSGCheck check={eventFeesEnabled} />
            </div>
            <p
              className="text-gray-font font-[400] text-base cursor-pointer"
              onClick={() => {
                const newValue = !eventFeesEnabled;
                setEventFeesEnabled(newValue);
                saveSectionStateToLocalStorage("fees", newValue);
                
                if (!newValue) {
                  setErrors({
                    ...errors,
                    currency: false,
                    memberFee: false,
                    memberLateFee: false,
                    guestLateFee: false,
                    cancellationPolicy: false,
                    partialRefund: false,
                    fullRefundEndDate: false,
                    partialRefundEndDate: false,
                  });
                }
              }}
            >
              Add Event Fees
            </p>
          </div>
          <div className="flex flex-row items-center gap-2">
            <div
              onClick={() => {
                const newValue = !registrationExpirationEnabled;
                setRegistrationExpirationEnabled(newValue);
                saveSectionStateToLocalStorage("registration", newValue);
                
                if (!newValue) {
                  setErrors({ ...errors, registrationExpiration: false });
                }
              }}
              className={`stroke-gray-font stroke-2 ${
                !registrationExpirationEnabled
                  ? "fill-neutral"
                  : "fill-secondary"
              } h-5 w-5 min-w-[20px] min-h-[20px] cursor-pointer`}
            >
              <WSGCheck check={registrationExpirationEnabled} />
            </div>
            <p className="text-gray-font font-[400] text-base">
              Registration Expiration
            </p>
          </div>
        </div>
      </div>
      {registrationExpirationEnabled && (
        <div className="w-full mb-6">
          <p className="text-primary font-decimal text-[26px] font-medium leading-[36px] text-left mb-6">
            Registration Expiration
          </p>
          <div className="md:w-[250px] w-full">
            <CalendarInput
              title="Registration Expiration"
              value={registrationExpiration}
              defaultValue={registrationExpiration}
              onChange={(date: any) => {
                setRegistrationExpiration(date);
                if (errors.registrationExpiration) {
                  setErrors({ ...errors, registrationExpiration: false });
                }
              }}
              type="single"
              error={errors.registrationExpiration}
              errorMsj={
                errors.registrationExpiration
                  ? "Registration Expiration is required"
                  : undefined
              }
              required
            />
          </div>
        </div>
      )}
      <div className="w-full mb-6">
        <p className="text-primary font-decimal text-[26px] font-medium leading-[36px] text-left mb-6">
          Registration Message
        </p>
        <TextArea
          title="Registration Message"
          value={registrationMessage}
          onChange={(e: any) => setRegistrationMessage(e.target.value)}
          error={false}
        />
      </div>
      {waitListEnabled && (
        <div className="w-full mb-6">
          <p className="text-Default font-decimal text-[32px] font-medium leading-[36px] text-left mb-6">
            Wait List Settings
          </p>
          <p className="text-primary font-decimal text-[26px] font-medium leading-[36px] text-left mb-6">
            Capacity
          </p>
          <div className="flex flex-row items-center gap-2">
            <div
              onClick={() => {
                const newValue = !addGuestCapacityEnabled;
                setAddGuestCapacityEnabled(newValue);
                if (!newValue) {
                  setErrors({ ...errors, guestCapacity: false });
                }
              }}
              className={`stroke-gray-font stroke-2 ${
                !addGuestCapacityEnabled ? "fill-neutral" : "fill-secondary"
              } h-5 w-5 min-w-[20px] min-h-[20px] cursor-pointer`}
            >
              <WSGCheck check={addGuestCapacityEnabled} />
            </div>
            <p className="text-gray-font font-[400] text-base">
              Add Guest Capacity
            </p>
          </div>
          <div className="flex flex-col gap-2 md:gap-4 md:flex-row mt-4">
            <Input
              title="Event Capacity"
              value={eventCapacity}
              onChange={(e: any) => {
                setEventCapacity(e.target.value);
                if (errors.eventCapacity) {
                  setErrors({ ...errors, eventCapacity: false });
                }
              }}
              type="number"
              error={errors.eventCapacity}
              errorMsj={
                errors.eventCapacity ? "Event Capacity is required" : undefined
              }
              required
            />
            <Input
              title="Firm Capacity"
              value={firmCapacity}
              onChange={(e: any) => {
                setFirmCapacity(e.target.value);
                if (errors.firmCapacity) {
                  setErrors({ ...errors, firmCapacity: false });
                }
              }}
              type="number"
              error={errors.firmCapacity}
              errorMsj={
                errors.firmCapacity ? "Firm Capacity is required" : undefined
              }
              required
            />
            <Input
              title="Guest Capacity"
              value={guestCapacity}
              onChange={(e: any) => {
                setGuestCapacity(e.target.value);
                if (errors.guestCapacity) {
                  setErrors({ ...errors, guestCapacity: false });
                }
              }}
              type="number"
              error={errors.guestCapacity}
              errorMsj={
                errors.guestCapacity ? "Guest Capacity is required" : undefined
              }
              required
              disabled={!addGuestCapacityEnabled}
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
            <TextArea
              title="Wait List Message"
              value={waitListMessage}
              onChange={(e: any) => {
                setWaitListMessage(e.target.value);
                if (errors.waitListMessage) {
                  setErrors({ ...errors, waitListMessage: false });
                }
              }}
              error={errors.waitListMessage}
              errorMsj={
                errors.waitListMessage
                  ? "Wait List Message is required"
                  : undefined
              }
              required
            />
            <TextArea
              title="Capacity Message"
              value={capacityMessage}
              onChange={(e: any) => {
                setCapacityMessage(e.target.value);
                if (errors.capacityMessage) {
                  setErrors({ ...errors, capacityMessage: false });
                }
              }}
              error={errors.capacityMessage}
              errorMsj={
                errors.capacityMessage
                  ? "Capacity Message is required"
                  : undefined
              }
              required
            />
            <TextArea
              title="Disclaim Capacity Message"
              value={disclaimCapacityMessage}
              onChange={(e: any) => {
                setDisclaimCapacityMessage(e.target.value);
                if (errors.disclaimCapacityMessage) {
                  setErrors({ ...errors, disclaimCapacityMessage: false });
                }
              }}
              error={errors.disclaimCapacityMessage}
              errorMsj={
                errors.disclaimCapacityMessage
                  ? "Disclaim Capacity Message is required"
                  : undefined
              }
              required
            />
          </div>
        </div>
      )}
      {eventFeesEnabled && (
        <div className="w-full mb-6">
          <p className="text-Default font-decimal text-[32px] font-medium leading-[36px] text-left mb-6">
            Modify Delegate Registration Fees
          </p>
          <p className="text-primary font-decimal text-[26px] font-medium leading-[36px] text-left mb-6">
            Registration Fee
          </p>
          <div className="flex flex-row items-center gap-2">
            <div
              onClick={() => {
                const newValue = !addLateFeesEnabled;
                setAddLateFeesEnabled(newValue);
                if (!newValue) {
                  setErrors({
                    ...errors,
                    memberLateFee: false,
                    guestLateFee: false,
                  });
                }
              }}
              className={`stroke-gray-font stroke-2 ${
                !addLateFeesEnabled ? "fill-neutral" : "fill-secondary"
              } h-5 w-5 min-w-[20px] min-h-[20px] cursor-pointer`}
            >
              <WSGCheck check={addLateFeesEnabled} />
            </div>
            <p className="text-gray-font font-[400] text-base">Add Late Fees</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4">
            <Dropdown
              title="Currency"
              data={CURRENCY_OPTIONS.map((curr) => ({ id: curr, name: curr }))}
              selectedObjst={currency || "USD"}
              seter={(selectedCurrency: any) => {
                if (selectedCurrency && selectedCurrency.id) {
                  setCurrency(selectedCurrency.id);
                } else if (typeof selectedCurrency === "string") {
                  setCurrency(selectedCurrency);
                }
                if (errors.currency) {
                  setErrors({ ...errors, currency: false });
                }
              }}
              getid
              placeholder="Select Currency"
              required
              error={errors.currency}
              errormsj={errors.currency ? "Currency is required" : undefined}
            />
            <Input
              title="Member Fee"
              value={memberFee}
              onChange={(e: any) => {
                setMemberFee(e.target.value);
                if (errors.memberFee) {
                  setErrors({ ...errors, memberFee: false });
                }
              }}
              type="number"
              error={errors.memberFee}
              errorMsj={errors.memberFee ? "Member Fee is required" : undefined}
              required
            />
            <Input
              title="Member Late Fee"
              value={memberLateFee}
              onChange={(e: any) => {
                setMemberLateFee(e.target.value);
                if (errors.memberLateFee) {
                  setErrors({ ...errors, memberLateFee: false });
                }
              }}
              type="number"
              error={errors.memberLateFee}
              errorMsj={
                errors.memberLateFee ? "Member Late Fee is required" : undefined
              }
              required={addLateFeesEnabled}
              disabled={!addLateFeesEnabled}
            />
            <Input
              title="Guest Late Fee"
              value={guestLateFee}
              onChange={(e: any) => {
                setGuestLateFee(e.target.value);
                if (errors.guestLateFee) {
                  setErrors({ ...errors, guestLateFee: false });
                }
              }}
              type="number"
              error={errors.guestLateFee}
              errorMsj={
                errors.guestLateFee
                  ? "Guest Late Fee is required when Guest Fee has a value"
                  : undefined
              }
              required={
                addLateFeesEnabled && !!guestFee && guestFee.trim() !== ""
              }
              disabled={!addLateFeesEnabled}
            />
            <Input
              title="Guest Fee"
              value={guestFee}
              onChange={(e: any) => {
                setGuestFee(e.target.value);
                if (!e.target.value || e.target.value.trim() === "") {
                  if (errors.guestLateFee) {
                    setErrors({ ...errors, guestLateFee: false });
                  }
                }
              }}
              type="number"
              error={false}
            />
          </div>
          {event.program && event.program.length > 0 && event.program.some((program) => program.requiredRegistration) && (
            <>
              {" "}
              <p className="text-primary font-decimal text-[26px] font-medium leading-[36px] text-left mb-6 mt-4">
                Special Event Fee
              </p>
              {event.program?.map((program, index) => (
                <div key={program.title}>
                  {program.requiredRegistration && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-4">
                      <div className="col-span-2">
                        <Input
                          title="Special Event Title"
                          type="text"
                          error={false}
                          disabled
                          value={program.registrationPageSessionTitle}
                        />
                      </div>
                      <Input
                        title="Fee"
                        type="number"
                        error={false}
                        value={programFees[index] || ""}
                        onChange={(e: any) =>
                          setProgramFees({
                            ...programFees,
                            [index]: e.target.value,
                          })
                        }
                      />
                      <Input
                        title="Guest Fee"
                        type="number"
                        error={false}
                        value={programGuestFees[index] || ""}
                        onChange={(e: any) =>
                          setProgramGuestFees({
                            ...programGuestFees,
                            [index]: e.target.value,
                          })
                        }
                      />
                    </div>
                  )}
                </div>
              ))}
            </>
          )}
          <div className="w-full mb-6 mt-6">
            <p className="text-primary font-decimal text-[26px] font-medium leading-[36px] text-left mb-6">
              Cancellation Policy
            </p>
            <TextArea
              title="Refund Policy"
              value={cancellationPolicy}
              onChange={(e: any) => {
                setCancellationPolicy(e.target.value);
                if (errors.cancellationPolicy) {
                  setErrors({ ...errors, cancellationPolicy: false });
                }
              }}
              error={false}
            />
            <div className="flex flex-col md:flex-row gap-2 mt-4">
              <Input
                title="Partial Refund %"
                type="number"
                error={Boolean(
                  errors.partialRefund ||
                  (partialRefund && partialRefund.trim() !== "" && (Number(partialRefund) < 0 || Number(partialRefund) > 100))
                )}
                value={partialRefund}
                onChange={(e: any) => {
                  if (e.target.value < 0 || e.target.value > 100) {
                    setPartialRefund("");
                  } else {
                    setPartialRefund(e.target.value);
                  }
                  if (errors.partialRefund) {
                    setErrors({ ...errors, partialRefund: false });
                  }
                }}
                errorMsj={
                  errors.partialRefund
                    ? "Partial Refund % must be a valid number"
                    : partialRefund && partialRefund.trim() !== "" && (Number(partialRefund) < 0 || Number(partialRefund) > 100)
                    ? "Partial Refund must be between 0 and 100"
                    : undefined
                }
              />
              <CalendarInput
                title="Full Refund End Date"
                value={fullRefundEndDate}
                defaultValue={fullRefundEndDate}
                onChange={(date: any) => {
                  setFullRefundEndDate(date);
                  if (errors.fullRefundEndDate) {
                    setErrors({ ...errors, fullRefundEndDate: false });
                  }
                }}
                type="single"
                error={errors.fullRefundEndDate}
                errorMsj={
                  errors.fullRefundEndDate
                    ? "Full Refund End Date must be a valid date"
                    : undefined
                }
              />
              <CalendarInput
                title="Partial Refund End Date"
                value={partialRefundEndDate}
                defaultValue={partialRefundEndDate}
                onChange={(date: any) => {
                  setPartialRefundEndDate(date);
                  if (errors.partialRefundEndDate) {
                    setErrors({ ...errors, partialRefundEndDate: false });
                  }
                }}
                type="single"
                error={errors.partialRefundEndDate}
                errorMsj={
                  errors.partialRefundEndDate
                    ? "Partial Refund End Date must be a valid date"
                    : undefined
                }
              />
            </div>
            <div className="flex flex-col md:flex-row gap-2 md:gap-8 mt-4">
              <CalendarInput
                title="Partial Refund Start Date"
                value={partialRefundStartDate}
                defaultValue={partialRefundStartDate}
                onChange={() => {}}
                type="single"
                disabled
              />
              <CalendarInput
                title="No Refund Start Date / Late Fee Start Date"
                value={noRefundStartDate}
                defaultValue={noRefundStartDate}
                onChange={() => {}}
                type="single"
                disabled
              />
            </div>
          </div>
        </div>
      )}
      <div className="w-full mb-6 flex flex-row gap-4 justify-end">
        <LegacyButton
          text="Cancel"
          icon={<Close className="w-5 h-5 [&>svg>path]:fill-current ml-2" />}
          color="neutral"
          style="outlined"
          size="small"
          content="textIcon"
          iconOrder="textFirst"
          onClick={handleCancel}
        />
        <LegacyButton
          text="SAVE"
          icon={
            <div className="save-icon-white ml-2">
              <SaveChanges />
            </div>
          }
          color="accent"
          style="filled"
          size="small"
          content="textIcon"
          iconOrder="textFirst"
          onClick={handleSave}
        />
      </div>
    </>
  );
}
